import React, { useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Mail, ArrowLeft, ShieldAlert } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '@/stores/authStore';

const ResendVerification: React.FC = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const logout = useAuthStore(state => state.logout);
    const user = useAuthStore(state => state.user);
    const isUnverifiedAccess = location.state?.unverified || false;
    
    const initialEmail = searchParams.get('email') || location.state?.email || user?.email || '';

    const [email, setEmail] = useState(initialEmail);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            setError('Veuillez saisir votre adresse email');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await api.post('/auth/resend-verification', { email });
            setSent(true);
        } catch (err: any) {
            console.error('Resend verification error:', err);
            setError(
                err.response?.data?.detail ||
                "Une erreur est survenue lors de l'envoi de l'email de vérification"
            );
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center space-y-4 p-6">
                        <CheckCircle className="h-12 w-12 text-green-600" />
                        <div className="text-center space-y-2">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Email envoyé !
                            </h2>
                            <p className="text-gray-600">
                                Si un compte existe avec cette adresse et qu’il n’est pas encore vérifié, un nouveau lien vient d’être envoyé.
                            </p>
                        </div>
                        <div className="w-full space-y-2">
                            <Button
                                className="w-full"
                                variant="outline"
                                onClick={() => {
                                    setSent(false);
                                }}
                            >
                                Renvoyer encore
                            </Button>
                            <Link to="/login" className="block">
                                <Button className="w-full" variant="ghost">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Retour à la connexion
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    {isUnverifiedAccess ? (
                        <div className="mx-auto mb-4 p-3 bg-orange-100 rounded-full w-fit">
                            <ShieldAlert className="h-12 w-12 text-orange-600" />
                        </div>
                    ) : (
                        <div className="mx-auto mb-4">
                            <Mail className="h-12 w-12 text-emerald-600" />
                        </div>
                    )}
                    <CardTitle className="text-2xl">
                        {isUnverifiedAccess ? 'Compte en cours de validation' : 'Renvoyer le lien de vérification'}
                    </CardTitle>
                    <p className="text-gray-600">
                        {isUnverifiedAccess 
                            ? "Votre adresse email n'a pas encore été vérifiée. Un email de validation vous a été envoyé lors de votre inscription."
                            : "Saisissez votre email pour recevoir un nouveau lien de vérification."}
                    </p>
                    {isUnverifiedAccess && (
                        <p className="text-sm text-orange-700 bg-orange-50 p-3 rounded-md mt-4 border border-orange-200">
                            Si vous n'avez pas reçu l'email, vous pouvez en demander un nouveau ci-dessous.
                        </p>
                    )}
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <XCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Adresse email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="votre@email.com"
                                required
                            />
                        </div>

                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Envoi...
                                </>
                            ) : (
                                'Envoyer le lien'
                            )}
                        </Button>

                        <div className="pt-2">
                            <Button 
                                type="button" 
                                className="w-full" 
                                variant="ghost"
                                onClick={() => logout()}
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Retour à la connexion
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResendVerification;
